import React, {createContext, useContext, useMemo, useState, ReactNode} from 'react';
import {ColorSchemeName, useColorScheme} from 'react-native';

type ThemeColors = {
  background: string;
  surface: string;
  text: string;
  mutedText: string;
  primary: string;
  border: string;
};

type Theme = {
  mode: 'light' | 'dark';
  colors: ThemeColors;
};

type ThemeContextValue = {
  theme: Theme;
  toggleTheme: () => void;
};

const lightColors: ThemeColors = {
  background: '#FFFFFF',
  surface: '#F6F7F9',
  text: '#111827',
  mutedText: '#6B7280',
  primary: '#2563EB',
  border: '#E5E7EB',
};

const darkColors: ThemeColors = {
  background: '#0B0F14',
  surface: '#121723',
  text: '#E5E7EB',
  mutedText: '#9CA3AF',
  primary: '#60A5FA',
  border: '#1F2937',
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({children}: {children: ReactNode}) {
  const system = useColorScheme();
  const [mode, setMode] = useState<Exclude<ColorSchemeName, 'no-preference'> | 'light' | 'dark'>(system || 'light');

  const theme = useMemo<Theme>(() => ({
    mode: mode === 'dark' ? 'dark' : 'light',
    colors: mode === 'dark' ? darkColors : lightColors,
  }), [mode]);

  const toggleTheme = () => setMode(prev => (prev === 'dark' ? 'light' : 'dark'));

  const value = useMemo(() => ({theme, toggleTheme}), [theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

